/**
 * Security utilities for the application
 */

/**
 * Sanitizes user input to prevent XSS attacks
 * @param {string} input - The user input to sanitize
 * @returns {string} - The sanitized input
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return '';
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

/**
 * Creates a nonce for CSP
 * @returns {string} - A random nonce value
 */
export const generateNonce = () => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
};

/**
 * Validates a URL to prevent open redirect vulnerabilities
 * @param {string} url - The URL to validate
 * @returns {boolean} - Whether the URL is safe
 */
export const isValidUrl = (url) => {
  try {
    const parsedUrl = new URL(url);
    // Only allow specific domains
    return ['cookiepal.eu', 'www.cookiepal.eu'].includes(parsedUrl.hostname);
  } catch (e) {
    return false;
  }
};

/**
 * Implements rate limiting for API calls
 */
export class RateLimiter {
  constructor(maxRequests = 10, timeWindow = 60000) {
    this.requests = new Map();
    this.maxRequests = maxRequests;
    this.timeWindow = timeWindow;
  }

  /**
   * Checks if a request should be allowed based on rate limiting
   * @param {string} clientId - Unique identifier for the client
   * @returns {boolean} - Whether the request should be allowed
   */
  allowRequest(clientId) {
    const now = Date.now();
    const clientRequests = this.requests.get(clientId) || [];
    
    // Remove expired timestamps
    const validRequests = clientRequests.filter(
      timestamp => now - timestamp < this.timeWindow
    );
    
    // Check if under limit
    if (validRequests.length < this.maxRequests) {
      validRequests.push(now);
      this.requests.set(clientId, validRequests);
      return true;
    }
    
    return false;
  }
}